import React, { useRef, useState } from "react";
import { Button, Input, Space, Switch } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { Game } from "../../../../services/gameQuiz/gameQuiz.types";
import ImageUploadInput from "../ImageUploadInput";
import TtsAudioControls from "../TtsAudioControls";
import { generateOptionId } from "../gameTypes";

type ImageHotspotGame = Extract<Game, { type: "image_hotspot" }>;
type Hotspot = ImageHotspotGame["hotspots"][number];

const MIN_DIMENSION = 0.02;

const CORRECT_COLOR = "#52c41a";
const CORRECT_FILL = "rgba(82,196,26,0.18)";
const INCORRECT_COLOR = "#ff4d4f";
const INCORRECT_FILL = "rgba(255,77,79,0.15)";

const clamp = (n: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, n));

type Props = {
  game: ImageHotspotGame;
  onChange: (game: ImageHotspotGame) => void;
  generateAudio?: (text: string) => Promise<string>;
};

type DragMode =
  | { kind: "draw"; originX: number; originY: number; id: string }
  | { kind: "move"; id: string; offsetX: number; offsetY: number }
  | { kind: "resize"; id: string };

const ImageHotspotEditor: React.FC<Props> = ({
  game,
  onChange,
  generateAudio,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragMode, setDragMode] = useState<DragMode | null>(null);

  // Defensive: partial/legacy games may arrive without these arrays.
  // Fall back to empty lists so the map() calls below don't crash.
  const hotspots: ImageHotspotGame["hotspots"] = Array.isArray(game.hotspots)
    ? game.hotspots
    : [];

  const updateHotspot = (id: string, patch: Partial<Hotspot>) => {
    onChange({
      ...game,
      hotspots: hotspots.map((h) =>
        h.id === id ? { ...h, ...patch } : h,
      ),
    });
  };

  const removeHotspot = (id: string) => {
    onChange({
      ...game,
      hotspots: hotspots.filter((h) => h.id !== id),
    });
  };

  const getNormalizedPoint = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: clamp((clientX - rect.left) / rect.width),
      y: clamp((clientY - rect.top) / rect.height),
    };
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!game.image) return;
    if (e.target !== e.currentTarget) return;
    const { x, y } = getNormalizedPoint(e.clientX, e.clientY);
    const id = generateOptionId();
    onChange({
      ...game,
      hotspots: [...hotspots, { id, x, y, width: 0, height: 0, correct: false }],
    });
    setDragMode({ kind: "draw", originX: x, originY: y, id });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragMode) return;
    const { x, y } = getNormalizedPoint(e.clientX, e.clientY);
    if (dragMode.kind === "draw") {
      const nx = Math.min(dragMode.originX, x);
      const ny = Math.min(dragMode.originY, y);
      const w = Math.abs(x - dragMode.originX);
      const h = Math.abs(y - dragMode.originY);
      updateHotspot(dragMode.id, { x: nx, y: ny, width: w, height: h });
    } else if (dragMode.kind === "move") {
      const hotspot = hotspots.find((h) => h.id === dragMode.id);
      if (!hotspot) return;
      const nx = clamp(x - dragMode.offsetX, 0, 1 - hotspot.width);
      const ny = clamp(y - dragMode.offsetY, 0, 1 - hotspot.height);
      updateHotspot(dragMode.id, { x: nx, y: ny });
    } else if (dragMode.kind === "resize") {
      const hotspot = hotspots.find((h) => h.id === dragMode.id);
      if (!hotspot) return;
      const w = clamp(x - hotspot.x, MIN_DIMENSION, 1 - hotspot.x);
      const h = clamp(y - hotspot.y, MIN_DIMENSION, 1 - hotspot.y);
      updateHotspot(dragMode.id, { width: w, height: h });
    }
  };

  const handleMouseUp = () => {
    if (dragMode?.kind === "draw") {
      const hotspot = hotspots.find((h) => h.id === dragMode.id);
      if (
        hotspot &&
        (hotspot.width < MIN_DIMENSION || hotspot.height < MIN_DIMENSION)
      ) {
        removeHotspot(dragMode.id);
      }
    }
    setDragMode(null);
  };

  const handleHotspotMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    hotspot: Hotspot,
  ) => {
    e.stopPropagation();
    const { x, y } = getNormalizedPoint(e.clientX, e.clientY);
    setDragMode({
      kind: "move",
      id: hotspot.id,
      offsetX: x - hotspot.x,
      offsetY: y - hotspot.y,
    });
  };

  const handleResizeMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    hotspot: Hotspot,
  ) => {
    e.stopPropagation();
    setDragMode({ kind: "resize", id: hotspot.id });
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div>
        <label>Pregunta</label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.question}
            onChange={(e) => onChange({ ...game, question: e.target.value })}
            rows={2}
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.question}
            audioUrl={game.questionAudio}
            onAudioChange={(url) => onChange({ ...game, questionAudio: url })}
            generateAudio={generateAudio}
          />
        </div>
      </div>

      <div>
        <label>Imagen base (obligatoria)</label>
        <ImageUploadInput
          value={game.image}
          onChange={(url) => onChange({ ...game, image: url ?? "" })}
        />
      </div>

      {game.image && (
        <div>
          <label>
            Áreas (arrastrá sobre la imagen para dibujar un rectángulo)
          </label>
          <div
            ref={canvasRef}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 720,
              userSelect: "none",
              border: "1px solid var(--bo-border-strong)",
              borderRadius: 6,
              overflow: "hidden",
              cursor: "crosshair",
            }}
          >
            <img
              src={game.image}
              alt="base"
              draggable={false}
              style={{ display: "block", width: "100%", pointerEvents: "none" }}
            />
            {hotspots.map((h, idx) => (
              <div
                key={h.id}
                onMouseDown={(e) => handleHotspotMouseDown(e, h)}
                style={{
                  position: "absolute",
                  left: `${h.x * 100}%`,
                  top: `${h.y * 100}%`,
                  width: `${h.width * 100}%`,
                  height: `${h.height * 100}%`,
                  border: `2px solid ${h.correct ? CORRECT_COLOR : INCORRECT_COLOR}`,
                  background: h.correct ? CORRECT_FILL : INCORRECT_FILL,
                  cursor: "move",
                  boxSizing: "border-box",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 2,
                    left: 4,
                    background: h.correct ? CORRECT_COLOR : INCORRECT_COLOR,
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "0 6px",
                    borderRadius: 4,
                    pointerEvents: "none",
                  }}
                >
                  {idx + 1}
                </span>
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeHotspot(h.id);
                  }}
                  style={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    padding: "0 4px",
                    height: 20,
                    minWidth: 20,
                  }}
                />
                <div
                  onMouseDown={(e) => handleResizeMouseDown(e, h)}
                  style={{
                    position: "absolute",
                    right: -6,
                    bottom: -6,
                    width: 12,
                    height: 12,
                    background: h.correct ? CORRECT_COLOR : INCORRECT_COLOR,
                    border: "2px solid #fff",
                    borderRadius: 2,
                    cursor: "nwse-resize",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {hotspots.length > 0 && (
        <div>
          <label>Áreas (marcá al menos una como correcta)</label>
          {hotspots.map((h, idx) => (
            <div
              key={h.id}
              style={{
                marginTop: 8,
                padding: 8,
                border: "1px solid var(--bo-card-border)",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span
                style={{
                  background: h.correct ? CORRECT_COLOR : INCORRECT_COLOR,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "0 8px",
                  borderRadius: 4,
                }}
              >
                {idx + 1}
              </span>
              <span style={{ flex: 1 }}>Área {idx + 1}</span>
              <Switch
                checked={h.correct}
                checkedChildren="Correcta"
                unCheckedChildren="Incorrecta"
                onChange={(checked) =>
                  updateHotspot(h.id, { correct: checked })
                }
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeHotspot(h.id)}
              />
            </div>
          ))}
        </div>
      )}
    </Space>
  );
};

export default ImageHotspotEditor;
